import type { ShakaPlayer } from '~/composables/useSharedVideoPlayer';
import type { Ref } from 'vue';

import {
	useElementHover,
	useEventListener,
	useIntersectionObserver,
	useTimeoutFn
} from '@vueuse/core';
import {
	getSavedBandwidthEstimate,
	registerActivePlayer,
	unregisterActivePlayer
} from '~/composables/useHlsPlayer/shakaLoader';
import {
	createPlayer,
	destroyPlayer
} from '~/composables/useSharedVideoPlayer';
import { useVideoQoE } from '~/composables/useVideoQoE';
import { getProxiedVideoUrl } from '~/composables/useVideoUrls';
import { wasVideoIntentPrefetched } from '~/composables/useVideoWarmup';
import { ErrorCategory } from '~/core/ports/datadog';
import { useDatadog } from '~~/composables/useDatadog';
import { computed } from 'vue';

import { isIOSNativePlayback } from '~/components/PropertyReelsFeed/useIOSSharedVideo';

// On slow networks, cards in the preload margin zone wait longer before
// initialising their player so the active card keeps the bandwidth.
function getMarginPreloadDelay(): number {
	const bps = getSavedBandwidthEstimate();
	if (bps === null || bps >= 4_000_000) return 600; // fast / unknown: standard
	if (bps >= 2_000_000) return 1_500; // medium: 1.5s
	return 3_000; // slow: 3s
}

/**
 * Awaits the first `canplay` or completes immediately if `signal` is aborted (ex.: unmount).
 * Always resolves; the caller must check `signal.aborted` and `shouldPlay` before `play()`.
 */
function waitUntilVideoCanPlay(
	video: HTMLVideoElement,
	signal: AbortSignal
): Promise<void> {
	if (video.readyState !== 0) return Promise.resolve();
	if (signal.aborted) return Promise.resolve();
	return new Promise(resolve => {
		const finish = () => resolve();
		video.addEventListener('canplay', finish, { once: true, signal });
		signal.addEventListener('abort', finish, { once: true });
	});
}

interface UseVideoCardPlayerOptions {
	videoUrl: Ref<string | null>;
	isActive: Ref<boolean>;
	hoverToPlay: Ref<boolean>;
	preloadWhenVisible?: Ref<boolean>;
	index: Ref<number>;
	playerRootMargin?: string;
	/** Fonte de hover externa. Quando omitido, usa `useElementHover(cardRef)`. */
	isHovered?: Ref<boolean>;
}

export function useVideoCardPlayer({
	videoUrl,
	isActive,
	hoverToPlay,
	preloadWhenVisible = computed(() => false),
	index,
	playerRootMargin = '100px',
	isHovered: externalHovered
}: UseVideoCardPlayerOptions) {
	const tag = () => `[Video #${index.value}]`;
	const { trackError } = useDatadog();

	function hasVideoSource(): boolean {
		return Boolean(videoUrl.value?.trim());
	}

	const cardRef = ref<HTMLDivElement | null>(null);
	const videoRef = ref<HTMLVideoElement | null>(null);
	const shakaPlayer = shallowRef<ShakaPlayer | null>(null);

	const isHovered = externalHovered ?? useElementHover(cardRef);
	const isVideoPlaying = ref(false);
	const isVisible = ref(false);
	const isPlayerReady = ref(false);
	const isInitializing = ref(false);
	const isAutoplayBlocked = ref(false);
	const videoProgress = ref(0);
	const isMuted = ref(true);
	let initGeneration = 0;
	let savedCurrentTime = 0;
	let initErrorCount = 0;
	const MAX_INIT_ERRORS = 3;
	/** In-flight `canplay` wait; aborted on unmount or superseded by a newer `safePlay`. */
	let pendingCanPlayAbort: AbortController | null = null;

	const shouldPlay = computed(
		() => (hoverToPlay.value && isHovered.value) || isActive.value
	);
	const shouldPreload = computed(
		() => hoverToPlay.value || preloadWhenVisible.value
	);
	const isLoading = computed(
		() => shouldPlay.value && !isVideoPlaying.value && !isAutoplayBlocked.value
	);
	const proxiedVideoUrl = computed(() => getProxiedVideoUrl(videoUrl.value));

	// QoE telemetry — carousel/card previews, separated from the feed via surface.
	const {
		beginAttempt: beginQoEAttempt,
		finalize: finalizeQoE,
		abort: abortQoE
	} = useVideoQoE({
		videoElement: videoRef,
		surface: 'shorts_carousel',
		pipeline: isIOSNativePlayback() ? 'ios_native' : 'shaka',
		getVideoUrl: () => proxiedVideoUrl.value,
		getShakaPlayer: () => shakaPlayer.value,
		wasWarmed: wasVideoIntentPrefetched
	});

	function handleMuteToggle() {
		isMuted.value = !isMuted.value;
		if (videoRef.value) {
			videoRef.value.muted = isMuted.value;
		}
	}

	function seekTo(ratio: number) {
		if (videoRef.value && videoRef.value.duration) {
			videoRef.value.currentTime = ratio * videoRef.value.duration;
		}
	}

	async function safePlay(video: HTMLVideoElement) {
		if (video.readyState === 0) {
			pendingCanPlayAbort?.abort();
			const canPlayWait = new AbortController();
			pendingCanPlayAbort = canPlayWait;
			try {
				await waitUntilVideoCanPlay(video, canPlayWait.signal);
			} finally {
				if (pendingCanPlayAbort === canPlayWait) {
					pendingCanPlayAbort = null;
				}
			}
			if (canPlayWait.signal.aborted || !shouldPlay.value) return;
		}

		isAutoplayBlocked.value = false;

		try {
			await video.play();
		} catch (err) {
			if (err instanceof DOMException && err.name === 'NotAllowedError') {
				video.muted = true;
				isMuted.value = true;
				try {
					await video.play();
				} catch (retryErr) {
					if (
						retryErr instanceof DOMException &&
						retryErr.name === 'NotAllowedError'
					) {
						isAutoplayBlocked.value = true;
						// QoE clock would count the wait for the user's tap — discard.
						abortQoE();
					}
					console.error(tag(), 'safePlay: muted retry also failed', retryErr);
				}
			} else {
				console.error(tag(), 'safePlay: play() failed', err);
			}
		}
	}

	useEventListener(videoRef, 'playing', () => {
		isVideoPlaying.value = true;
		isAutoplayBlocked.value = false;
	});

	useEventListener(videoRef, 'pause', () => {
		isVideoPlaying.value = false;
	});

	useEventListener(videoRef, 'error', () => {
		const err = videoRef.value?.error;
		finalizeQoE('error');
		console.error(tag(), 'EVENT error', {
			code: err?.code,
			message: err?.message
		});
	});

	useEventListener(videoRef, 'timeupdate', () => {
		if (videoRef.value && videoRef.value.duration) {
			videoProgress.value =
				(videoRef.value.currentTime / videoRef.value.duration) * 100;
		}
	});

	// Cards visible on screen init fast; cards only in the preload margin wait
	// longer — and scale the delay to the saved network speed so slow-network
	// users don't waste bandwidth on off-screen cards while the active one buffers.
	const { start: startPreloadVisible, stop: stopPreloadVisible } = useTimeoutFn(
		() => {
			if (!isPlayerReady.value && hasVideoSource()) initPlayer();
		},
		100,
		{ immediate: false }
	);

	let preloadMarginTimer: ReturnType<typeof setTimeout> | null = null;

	function startPreloadMargin() {
		if (preloadMarginTimer) clearTimeout(preloadMarginTimer);
		preloadMarginTimer = setTimeout(() => {
			preloadMarginTimer = null;
			if (!isPlayerReady.value && hasVideoSource()) initPlayer();
		}, getMarginPreloadDelay());
	}

	function stopPreloadMargin() {
		if (preloadMarginTimer) {
			clearTimeout(preloadMarginTimer);
			preloadMarginTimer = null;
		}
	}

	function startPreload(ratio: number) {
		if (ratio > 0) {
			stopPreloadMargin();
			startPreloadVisible();
		} else {
			startPreloadMargin();
		}
	}

	function stopPreload() {
		stopPreloadVisible();
		stopPreloadMargin();
	}

	useIntersectionObserver(
		cardRef,
		entries => {
			const entry = entries[0];
			if (!entry) return;

			if (entry.isIntersecting) {
				isVisible.value = entry.intersectionRatio > 0;
				if (!hasVideoSource()) return;
				// Re-entering the viewport with shouldPlay still true replays the
				// video without the watcher refiring — reopen the QoE attempt
				// (idempotent while one is already running for this URL).
				if (shouldPlay.value) beginQoEAttempt();
				if (shouldPreload.value) {
					startPreload(entry.intersectionRatio);
				} else if (shouldPlay.value && !isPlayerReady.value) {
					cancelPlayDebounce();
					startPlayDebounce();
				}
			} else {
				isVisible.value = false;
				cancelPlayDebounce();
				stopPreload();
				// Scrolled out of view while playing — the play session is over.
				finalizeQoE('navigated');
				cleanupPlayer();
			}
		},
		{ rootMargin: playerRootMargin, threshold: [0, 0.01] }
	);

	async function initPlayer() {
		if (
			!hasVideoSource() ||
			!proxiedVideoUrl.value ||
			!videoRef.value ||
			isPlayerReady.value ||
			isInitializing.value ||
			initErrorCount >= MAX_INIT_ERRORS
		)
			return;

		const initStart = performance.now();

		isInitializing.value = true;
		const generation = ++initGeneration;

		try {
			const startTime = savedCurrentTime > 0 ? savedCurrentTime : undefined;
			const url = proxiedVideoUrl.value;

			const player = await createPlayer(url, videoRef.value, {
				startTime
			});

			if (generation !== initGeneration || !videoRef.value) {
				destroyPlayer(player);
				return;
			}

			shakaPlayer.value = player;
			isPlayerReady.value = true;

			if (shouldPlay.value && videoRef.value) safePlay(videoRef.value);
		} catch (err) {
			// AbortError = intentional cancel (scroll, timeout) — allow retry, no log.
			const isAbort = err instanceof DOMException && err.name === 'AbortError';
			if (isAbort) return;

			initErrorCount++;
			const elapsed = Math.round(performance.now() - initStart);
			console.error(
				tag(),
				`initPlayer ERROR after ${elapsed}ms (attempt ${initErrorCount}/${MAX_INIT_ERRORS})`,
				err
			);
			trackError({
				message: `[useVideoCardPlayer] initPlayer failed after ${elapsed}ms (attempt ${initErrorCount}/${MAX_INIT_ERRORS}): ${(err as Error)?.message}`,
				source: 'source',
				category: ErrorCategory.UI,
				stack: (err as Error)?.stack
			});
		} finally {
			isInitializing.value = false;
		}
	}

	function cleanupPlayer() {
		initGeneration++;
		initErrorCount = 0;
		if (shakaPlayer.value) {
			if (videoRef.value) savedCurrentTime = videoRef.value.currentTime;
			destroyPlayer(shakaPlayer.value, videoRef.value ?? undefined);
			shakaPlayer.value = null;
			isPlayerReady.value = false;
			isVideoPlaying.value = false;
			isAutoplayBlocked.value = false;
		}
	}

	const PLAY_DEBOUNCE_MS = 200;

	const { start: startPlayDebounce, stop: cancelPlayDebounce } = useTimeoutFn(
		() => {
			if (shouldPlay.value && !isPlayerReady.value && videoRef.value) {
				stopPreload();
				initPlayer();
			}
		},
		PLAY_DEBOUNCE_MS,
		{ immediate: false }
	);

	watch(
		shouldPlay,
		playing => {
			cancelPlayDebounce();

			const url = proxiedVideoUrl.value;
			if (url) {
				if (playing) registerActivePlayer(url);
				else unregisterActivePlayer(url);
			}

			// QoE attempt tracks the should-play window (activation -> deactivation).
			if (playing && hasVideoSource()) beginQoEAttempt();
			else if (!playing) finalizeQoE('navigated');

			if (!hasVideoSource() || !videoRef.value) return;

			if (playing) {
				if (isPlayerReady.value) {
					safePlay(videoRef.value);
				} else {
					startPlayDebounce();
				}
			} else if (isPlayerReady.value) {
				videoRef.value.pause();
			}
		},
		{ immediate: true }
	);

	onBeforeUnmount(() => {
		pendingCanPlayAbort?.abort();
		pendingCanPlayAbort = null;
		cancelPlayDebounce();
		stopPreload(); // already calls stopPreloadMargin internally
		if (proxiedVideoUrl.value) unregisterActivePlayer(proxiedVideoUrl.value);
		cleanupPlayer();
	});

	return {
		cardRef,
		videoRef,
		isVideoPlaying,
		isVisible,
		isLoading,
		isInitializing,
		isAutoplayBlocked,
		shouldPlay,
		videoProgress,
		isMuted,
		handleMuteToggle,
		seekTo
	};
}
